import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCents } from '@/lib/format/currency';
import { cn } from '@/lib/utils';
import { describeStatementLine, formatSignedCents, type StatementLineCopy } from '@/lib/wallet/describeStatementLine';
import { formatStatementDateTime, formatStatementDay, formatStatementTime } from '@/lib/wallet/formatStatementDates';
import { buildStatementPeriods, formatPeriodMonth } from '@/lib/wallet/statementPeriods';
import type { MyWalletStatementData, MyWalletStatementVars, WalletStatementLine } from '@/lib/wallet/statementTypes';
import { MY_WALLET_STATEMENT } from '@/queries/walletStatement';

export const WalletStatement = ({
  title,
  id,
  refreshKey = 0,
}: {
  title: string;
  id?: string;
  // Bump after an action on the same page that moves the balance (payout
  // request, hold cancel): the client is no-cache, but nothing else refetches.
  refreshKey?: number;
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const periods = useMemo(() => buildStatementPeriods(new Date()), []);
  const [periodKey, setPeriodKey] = useState('last30');
  const period = periods.find((entry) => entry.key === periodKey) ?? periods[0];

  const { data, loading, error, refetch } = useQuery<MyWalletStatementData, MyWalletStatementVars>(
    MY_WALLET_STATEMENT,
    { variables: { from: period.from, to: period.to } },
  );
  const statement = data?.myWalletStatement;

  const lastRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (lastRefreshKey.current === refreshKey) return;
    lastRefreshKey.current = refreshKey;
    refetch();
  }, [refreshKey, refetch]);

  // /earn/wallet#extrato links (affiliate and royalty pages) land on the card.
  useEffect(() => {
    if (!id || !statement || window.location.hash !== `#${id}`) return;
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [id, statement]);

  const copy = (entry: StatementLineCopy) => t(entry.key, entry.values);
  const formatDay = (day: string) => formatStatementDay(day, locale);

  const renderLine = (line: WalletStatementLine, timeLabel: string, today: string) => {
    const description = describeStatementLine(line, { today, formatDay });
    return (
      <li key={line.id} data-testid="wallet-statement-line" className="py-2 sm:flex sm:items-start sm:gap-3">
        <span className="block text-xs tabular-nums text-muted-foreground sm:w-28 sm:shrink-0">{timeLabel}</span>
        <div className="min-w-0 sm:flex-1">
          <p className="text-sm font-medium break-words">{copy(description.title)}</p>
          {description.detail && (
            <p className="text-xs text-muted-foreground break-words">{copy(description.detail)}</p>
          )}
        </div>
        <span
          className={cn(
            'mt-0.5 block text-sm font-semibold tabular-nums sm:mt-0 sm:shrink-0 sm:text-right',
            description.tone === 'credit' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
          )}
        >
          {formatSignedCents(line.amount_cents)}
        </span>
      </li>
    );
  };

  const hasLines = statement ? statement.days.some((day) => day.lines.length > 0) : false;

  return (
    <Card id={id} className="scroll-mt-24">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle>{title}</CardTitle>
        <Select value={period.key} onValueChange={setPeriodKey}>
          <SelectTrigger className="w-52" aria-label={t('walletStatement.period.label')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {periods.map((entry) => (
              <SelectItem key={entry.key} value={entry.key}>
                {entry.month ? formatPeriodMonth(entry.month, locale) : t('walletStatement.period.last30')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading && !statement && (
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {error && !statement && (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-destructive">{t('walletStatement.error')}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>{t('walletStatement.retry')}</Button>
          </div>
        )}

        {statement && (
          <>
            {statement.upcoming.length > 0 && (
              <section className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-3">
                <h3 className="text-sm font-semibold">{t('walletStatement.upcoming.title')}</h3>
                <p className="text-xs text-muted-foreground">{t('walletStatement.upcoming.hint')}</p>
                <ul className="mt-2 divide-y divide-border/60">
                  {statement.upcoming.map((line) => renderLine(
                    line,
                    line.at ? formatStatementDateTime(line.at, locale) : '',
                    statement.today,
                  ))}
                </ul>
              </section>
            )}

            {statement.days.map((day) => (
              <section key={day.day} className="space-y-1">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {formatDay(day.day)}
                  {day.day === statement.today ? ` · ${t('walletStatement.today')}` : ''}
                </h3>
                {day.lines.length > 0 && (
                  <ul className="divide-y divide-border/60">
                    {day.lines.map((line) => renderLine(
                      line,
                      line.at ? formatStatementTime(line.at, locale) : '',
                      statement.today,
                    ))}
                  </ul>
                )}
                <div className="flex items-center justify-between border-t border-border/60 pt-2 text-sm">
                  <span className="text-muted-foreground">
                    {day.day === statement.today ? t('walletStatement.currentBalance') : t('walletStatement.dayClosing')}
                  </span>
                  <span className="font-semibold tabular-nums">
                    {day.closing_balance_cents === null
                      ? t('walletStatement.noSnapshot')
                      : formatCents(day.closing_balance_cents, 'BRL')}
                  </span>
                </div>
              </section>
            ))}

            {!hasLines && statement.upcoming.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('walletStatement.empty')}</p>
            )}

            {statement.opening_balance_cents !== null && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{t('walletStatement.openingBalance')}</span>
                <span className="tabular-nums">{formatCents(statement.opening_balance_cents, 'BRL')}</span>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              {statement.first_snapshot_day
                ? t('walletStatement.snapshotsStarted', { date: formatDay(statement.first_snapshot_day) })
                : t('walletStatement.snapshotsPending')}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default WalletStatement;
